import { useCallback, useEffect, useState } from "react";
import { vehiclesApi } from "@services/api/vehicles";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";

export interface OrdreMobility {
  reservation: VehicleReservation;
  vehicle: Vehicle | null;
}

/**
 * Toutes les solutions de mobilité d'un ordre, la plus ancienne d'abord.
 *
 * Un ordre n'est pas limité à un véhicule de remplacement : un prêt du 1 au 8 peut être
 * suivi d'une location du 8 au 15. La source de vérité est donc `vehicle_reservations`
 * (`ordre_id`), pas le couple `rental_vehicle_id` / `rental_reservation_id` porté par
 * l'ordre — celui-ci reste le MIROIR de la mobilité courante, recalculé côté back, et
 * ne peut par nature en désigner qu'une.
 *
 * Les véhicules sont relus un par un : un ordre en porte une ou deux, charger les 500
 * véhicules de la flotte à chaque ouverture d'ordre coûterait bien plus cher.
 */
export function useOrdreMobilities(ordreId?: string) {
  const [mobilities, setMobilities] = useState<OrdreMobility[]>([]);

  const reload = useCallback(async () => {
    if (!ordreId) { setMobilities([]); return; }
    try {
      const reservations = await vehiclesApi.listReservations({ ordre_id: ordreId });
      const vehicles = await Promise.all(
        reservations.map((r) =>
          r.vehicleId ? vehiclesApi.get(r.vehicleId).catch(() => null) : Promise.resolve(null),
        ),
      );
      setMobilities(
        reservations
          .map((reservation, i) => ({ reservation, vehicle: vehicles[i] }))
          .sort((a, b) =>
            (a.reservation.startDate || "").localeCompare(b.reservation.startDate || "")
            || (a.reservation.startHour ?? 0) - (b.reservation.startHour ?? 0),
          ),
      );
    } catch {
      setMobilities([]);
    }
  }, [ordreId]);

  useEffect(() => { reload(); }, [reload]);

  return { mobilities, reloadMobilities: reload };
}
